import Swal from 'sweetalert2';
import i18n from 'i18n';
import './authAlerts.css';

export type AuthAlertAction = 'dismissed' | 'goToLogin';

type AuthAlertVariant = 'success' | 'notice' | 'error';

interface AuthAlertButton {
  labelKey: string;
  action: AuthAlertAction;
}

interface AuthAlertContent {
  variant: AuthAlertVariant;
  eyebrowKey: string;
  titleKey: string;
  paragraphKeys?: string[];
  email?: string;
  stepKeys?: string[];
  noteKey?: string;
  primary: AuthAlertButton;
  secondary?: AuthAlertButton;
  locked?: boolean;
}

const TITLE_ID = 'pf-alert-title';
const BODY_ID = 'pf-alert-body';

const BADGES: Record<AuthAlertVariant, string> = {
  success:
    '<svg viewBox="0 0 24 24"><path d="M3.5 12.5 9 18 20.5 6.5"/></svg>',
  notice:
    '<svg viewBox="0 0 24 24"><path d="M3 7.6A2.6 2.6 0 0 1 5.6 5h12.8A2.6 2.6 0 0 1 21 7.6v8.8a2.6 2.6 0 0 1-2.6 2.6H5.6A2.6 2.6 0 0 1 3 16.4V7.6Z"/><path d="m3.8 6.9 6.9 5.2a2.1 2.1 0 0 0 2.6 0l6.9-5.2"/></svg>',
  error:
    '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7.75v5"/><path d="M12 16.15h.01"/></svg>',
};

const MAIL_GLYPH =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="m3.8 6.9 6.9 5.2a2.1 2.1 0 0 0 2.6 0l6.9-5.2"/></svg>';

const t = (key: string) => String(i18n.t(key));

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const buildHtml = (content: AuthAlertContent) => {
  const paragraphs = (content.paragraphKeys ?? [])
    .map((key) => `<p>${t(key)}</p>`)
    .join('');

  const pill = content.email
    ? `<div class="pf-alert__pill">${MAIL_GLYPH}<span>${escapeHtml(
        content.email,
      )}</span></div>`
    : '';

  const steps = content.stepKeys?.length
    ? `<ol class="pf-alert__steps">${content.stepKeys
        .map((key) => `<li>${t(key)}</li>`)
        .join('')}</ol>`
    : '';

  const note = content.noteKey
    ? `<p class="pf-alert__note">${t(content.noteKey)}</p>`
    : '';

  return `
    <div class="pf-alert">
      <span class="pf-alert__badge" aria-hidden="true">${
        BADGES[content.variant]
      }</span>
      <p class="pf-alert__eyebrow">${t(content.eyebrowKey)}</p>
      <h2 class="pf-alert__title" id="${TITLE_ID}">${t(content.titleKey)}</h2>
      <div class="pf-alert__body" id="${BODY_ID}">${paragraphs}${pill}${steps}</div>
      ${note}
    </div>
  `;
};

const showAuthAlert = async (
  content: AuthAlertContent,
): Promise<AuthAlertAction> => {
  const result = await Swal.fire({
    html: buildHtml(content),
    showConfirmButton: true,
    confirmButtonText: t(content.primary.labelKey),
    showDenyButton: Boolean(content.secondary),
    denyButtonText: content.secondary ? t(content.secondary.labelKey) : '',
    showCloseButton: !content.locked,
    allowOutsideClick: !content.locked,
    allowEscapeKey: !content.locked,
    buttonsStyling: false,
    focusConfirm: true,
    customClass: {
      container: 'pf-swal-container',
      popup: `pf-swal-popup pf-swal-popup--${content.variant}`,
      htmlContainer: 'pf-swal-html',
      actions: 'pf-swal-actions',
      confirmButton: 'pf-swal-btn pf-swal-btn--primary',
      denyButton: 'pf-swal-btn pf-swal-btn--ghost',
      closeButton: 'pf-swal-close',
    },
    showClass: { popup: 'pf-swal-in', backdrop: 'pf-swal-backdrop-in' },
    hideClass: { popup: 'pf-swal-out', backdrop: 'pf-swal-backdrop-out' },
    didOpen: (popup) => {
      popup.setAttribute('aria-labelledby', TITLE_ID);
      popup.setAttribute('aria-describedby', BODY_ID);
    },
  });

  if (result.isConfirmed) return content.primary.action;
  if (result.isDenied && content.secondary) return content.secondary.action;
  return 'dismissed';
};

export const showSignUpSuccessAlert = (email: string) =>
  showAuthAlert({
    variant: 'success',
    eyebrowKey: 'authAlert.eyebrow.signUp',
    titleKey: 'authAlert.signUpSuccess.title',
    paragraphKeys: ['authAlert.signUpSuccess.intro'],
    email,
    stepKeys: [
      'authAlert.signUpSuccess.step1',
      'authAlert.signUpSuccess.step2',
      'authAlert.signUpSuccess.step3',
    ],
    noteKey: 'authAlert.signUpSuccess.note',
    primary: {
      labelKey: 'authAlert.actions.goToSignIn',
      action: 'goToLogin',
    },
    locked: true,
  });

export const showEmailVerifiedAlert = () =>
  showAuthAlert({
    variant: 'success',
    eyebrowKey: 'authAlert.eyebrow.emailVerification',
    titleKey: 'authAlert.emailVerified.title',
    paragraphKeys: ['authAlert.emailVerified.text'],
    primary: { labelKey: 'authAlert.actions.signInNow', action: 'dismissed' },
  });

